import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import { t } from 'ember-intl';
import { PhSlidersHorizontal } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import { or } from 'tio-ui/utilities';

interface PageControlBarSignature {
  Args: {
    /**
     * Result count rendered in the standardized right-aligned cluster.
     * `0` is shown; omit entirely to hide the count.
     */
    count?: number | string;
    /** Optional noun after the count, e.g. "people" → "1,204 people". */
    countLabel?: string;
    /** Opts filters into a collapsed disclosure below 64rem; defaults to false. */
    collapseFilters?: boolean;
    /** Caller-supplied active filter count, excluding search; only positive counts are shown. */
    activeFilterCount?: number;
    /** Emitted as `data-test-hook` on the filter disclosure button. */
    filterToggleTestHook?: string;
  };
  Blocks: {
    /**
     * Tab strip (e.g. `<NavTabs>`). When present alongside `:filters`,
     * `:trailing`, or `@count`, it renders as the first of two stacked bands.
     */
    tabs?: [];
    /** Left-aligned filter controls (selects, search, checkboxes). */
    filters?: [];
    /** Right-aligned controls after the count — sort select, "clear" button, etc. */
    trailing?: [];
  };
  Element: HTMLElement;
}

/**
 * PageControlBar – the conditional secondary band that pairs with `<PageHeader>`.
 *
 * Render it in `<PageHeader>`'s `:below` slot. It is the single, standardized home
 * for a page's filter cluster and/or tabs, plus a fixed-position result-count
 * cluster on the right. Page-specific controls go in the slots; the structure,
 * spacing, surface, and count styling stay identical on every page.
 *
 * Layout is driven entirely by `.app-page-control-bar*` recipe classes (not
 * Tailwind utilities), per the reimagined semantic-class direction.
 *
 * Layout rules (see the page-header playbook):
 * - Only appears once a page exceeds two inputs (search stays on the title row
 *   in `<PageHeader>`'s `:toolbar`) or has tabs.
 * - Tabs always live here, never on the title row.
 * - Tabs **and** filters → two stacked bands (tabs first), automatically.
 *
 * @example Filters + standardized count
 * ```gts
 * import PageHeader from 'tio-ui/components/page-header';
 * import PageControlBar from 'tio-ui/components/page-control-bar';
 *
 * <PageHeader @title="People" @icon={{PhUsers}}>
 *   <:toolbar><SearchInput … /></:toolbar>
 *   <:actions><Button @intent="primary">Create employee</Button></:actions>
 *   <:below>
 *     <PageControlBar @count={{@model.total}} @countLabel="people">
 *       <:filters>
 *         <SelectCompany … />
 *         <Select @label="Role" … />
 *       </:filters>
 *       <:trailing>
 *         <Button @appearance="minimal" @intent="danger">Clear all</Button>
 *       </:trailing>
 *     </PageControlBar>
 *   </:below>
 * </PageHeader>
 * ```
 *
 * @example Tabs band only
 * ```gts
 * <PageControlBar>
 *   <:tabs><NavTabs @tabs={{this.tabs}} /></:tabs>
 * </PageControlBar>
 * ```
 *
 * For a control owned by a deeply nested component the route template can't see,
 * portal it into the trailing area with `<PageControlBarItem>`.
 */
export default class PageControlBar extends Component<PageControlBarSignature> {
  @tracked filtersExpanded = false;

  readonly filtersId = `page-control-bar-filters-${guidFor(this)}`;

  get hasCount() {
    return this.args.count !== undefined && this.args.count !== null;
  }

  get hasActiveFilters() {
    return (this.args.activeFilterCount ?? 0) > 0;
  }

  /** Changes the compact disclosure state without unmounting the filter controls. */
  toggleFilters = () => {
    this.filtersExpanded = !this.filtersExpanded;
  };

  <template>
    <div data-test-page-control-bar class="app-page-control-bar" ...attributes>
      {{#if (has-block "tabs")}}
        <div data-test-page-control-bar-tabs class="app-page-control-bar__band">
          {{yield to="tabs"}}
        </div>
      {{/if}}

      {{#if (or (has-block "filters") (has-block "trailing") this.hasCount)}}
        <div
          data-test-page-control-bar-controls
          class="app-page-control-bar__band app-page-control-bar__controls
            {{if
              (and @collapseFilters (has-block 'filters'))
              'app-page-control-bar__controls--collapsible'
            }}"
        >
          {{#if (has-block "filters")}}
            {{#if @collapseFilters}}
              <Button
                @appearance="minimal"
                @size="sm"
                @type="button"
                @testHook={{@filterToggleTestHook}}
                class="app-page-control-bar__filter-toggle"
                data-test-page-control-bar-filter-toggle
                aria-controls={{this.filtersId}}
                aria-expanded={{if this.filtersExpanded "true" "false"}}
                {{on "click" this.toggleFilters}}
              >
                <PhSlidersHorizontal @size={{16}} aria-hidden="true" />
                {{if this.filtersExpanded (t "common.hide_filters") (t "common.more_filters")}}
                {{#if this.hasActiveFilters}}
                  <Chip @size="sm">{{@activeFilterCount}}</Chip>
                {{/if}}
              </Button>
            {{/if}}
            <div
              id={{if @collapseFilters this.filtersId}}
              class="app-page-control-bar__filters"
              data-filters-expanded={{if @collapseFilters (if this.filtersExpanded "true" "false")}}
            >
              {{yield to="filters"}}
            </div>
          {{/if}}

          <div class="app-page-control-bar__trailing">
            {{#if this.hasCount}}
              <span data-test-page-control-bar-count class="app-page-control-bar__count">
                <span class="app-page-control-bar__count-value">{{@count}}</span>
                {{~#if @countLabel}}&nbsp;{{@countLabel}}{{/if~}}
              </span>
            {{/if}}
            {{#if (has-block "trailing")}}
              {{yield to="trailing"}}
            {{/if}}
            {{! Portal target for <PageControlBarItem> — deep-child escape hatch }}
            <div id="page-control-bar-portal" class="app-page-control-bar__portal"></div>
          </div>
        </div>
      {{else if (has-block "tabs")}}
        {{! Tabs-only bar: keep the <PageControlBarItem> portal reachable }}
        <div id="page-control-bar-portal" class="app-page-control-bar__portal"></div>
      {{/if}}
    </div>
  </template>
}

export type { PageControlBarSignature };
